/**
 * Mediastata poll bridge (без их watcher-embed).
 *
 * Считаем прослушивания сами: conds → listener → trigger.
 * Popup (/embed/performer) — только когда trigger вернул true в этой сессии,
 * не при каждом F5 из‑за reach=true.
 */

const CALLOUT_LISTENER_KEY = '_callout-listener';
const API = 'https://vote.mediastata.com';
const PERFORMER_SHOWN_KEY = '_mediastata_performer_session';

const pendingAudioUrls = [];
let bridgeState = null; // { pollId, listenerId, placementCode, mounts }
let bridgePromise = null;
let triggerInFlight = false;
let performerStarted = false;

function ensureProcessPolyfill() {
  if (typeof window !== 'undefined' && typeof window.process === 'undefined') {
    window.process = { env: { NODE_ENV: 'production' } };
  }
}

function placementFromEmbedSrc(src) {
  try {
    const raw = String(src).trim();
    const fromAttr = raw.match(/src=["']([^"']+)["']/i);
    const urlStr = fromAttr ? fromAttr[1] : raw;
    const parts = new URL(urlStr, window.location.origin).pathname.split('/').filter(Boolean);
    return parts[parts.length - 1] || null;
  } catch (_) {
    return null;
  }
}

function normalizeEmbedSrc(src) {
  const raw = String(src).trim();
  const fromAttr = raw.match(/src=["']([^"']+)["']/i);
  return fromAttr ? fromAttr[1] : raw;
}

function readListenerId() {
  try {
    const fromLs = window.localStorage?.getItem(CALLOUT_LISTENER_KEY);
    if (fromLs) {
      const n = Number(fromLs);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
  } catch (_) {
    // ignore
  }
  try {
    const match = document.cookie.match(/(?:^|; )_callout-listener=([^;]*)/);
    if (match) {
      const n = Number(decodeURIComponent(match[1]));
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
  } catch (_) {
    // ignore
  }
  return null;
}

function writeListenerId(id) {
  const value = String(id);
  try {
    window.localStorage.setItem(CALLOUT_LISTENER_KEY, value);
  } catch (_) {
    // ignore
  }
  try {
    document.cookie = `${CALLOUT_LISTENER_KEY}=${encodeURIComponent(value)}; path=/; SameSite=None; Secure`;
  } catch (_) {
    // ignore
  }
}

async function apiJson(method, route, body) {
  const options = {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  };
  const res = await fetch(`${API}${route}`, options);
  if (!res.ok) {
    throw new Error(`mediastata ${method} ${route} → ${res.status}`);
  }
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
}

function toMountPath(streamUrl) {
  try {
    const u = new URL(streamUrl, window.location.origin);
    const match = u.pathname.match(/^(\/live\/[^/]+)\//);
    if (match) {
      return `${match[1]}/icecast.audio`;
    }
    if (/\/icecast\.audio$/i.test(u.pathname)) {
      return u.pathname;
    }
    return u.pathname;
  } catch (_) {
    return null;
  }
}

function mountMatches(audioUrl, mounts) {
  if (!mounts || !mounts.length) {
    return true;
  }
  const path = toMountPath(audioUrl);
  if (path && mounts.includes(path)) {
    return true;
  }
  return mounts.some((mount) => {
    const folder = String(mount).replace(/\/icecast\.audio$/i, '');
    return folder && String(audioUrl).includes(folder);
  });
}

function startPerformer(placementCode) {
  if (performerStarted) {
    return;
  }
  try {
    if (window.sessionStorage?.getItem(PERFORMER_SHOWN_KEY) === placementCode) {
      return;
    }
  } catch (_) {
    // ignore
  }
  if (document.querySelector('script[data-mediastata-performer="1"]')) {
    performerStarted = true;
    return;
  }
  performerStarted = true;
  try {
    window.sessionStorage?.setItem(PERFORMER_SHOWN_KEY, placementCode);
  } catch (_) {
    // ignore
  }
  const script = document.createElement('script');
  script.src = `${API}/embed/performer/${placementCode}`;
  script.async = true;
  script.dataset.mediastataPerformer = '1';
  document.body.appendChild(script);
}

async function fireTrigger(audioUrl) {
  if (!bridgeState) {
    pendingAudioUrls.push(audioUrl);
    return;
  }
  if (triggerInFlight) {
    return;
  }

  const { pollId, listenerId, placementCode, mounts } = bridgeState;
  if (!mountMatches(audioUrl, mounts)) {
    return;
  }

  triggerInFlight = true;
  try {
    const reached = await apiJson('PUT', '/public/watcher/trigger', {
      pollId,
      listenerId,
    });
    // true = порог в кабинете mediastata достигнут.
    if (reached === true) {
      startPerformer(placementCode);
    }
  } catch (_) {
    // ignore
  } finally {
    triggerInFlight = false;
  }
}

function installNotifyStub() {
  if (typeof window === 'undefined') {
    return;
  }
  if (typeof window.__mediastataBridgeNotify === 'function' && window.__mediastataBridgeNotify.__ready) {
    return;
  }
  const stub = (audioUrl) => {
    pendingAudioUrls.push(audioUrl);
  };
  stub.__ready = false;
  window.__mediastataBridgeNotify = stub;
}

function installNotifyReady() {
  const ready = (audioUrl) => {
    fireTrigger(audioUrl);
  };
  ready.__ready = true;
  window.__mediastataBridgeNotify = ready;

  if (typeof window.__onAudioStarted !== 'function' || window.__onAudioStarted.__mediastataBridge) {
    const hook = (url) => {
      window.__mediastataBridgeNotify?.(url);
    };
    hook.__mediastataBridge = true;
    window.__onAudioStarted = hook;
  }

  while (pendingAudioUrls.length) {
    fireTrigger(pendingAudioUrls.shift());
  }
}

async function startMediastataBridge(placementCode) {
  installNotifyStub();

  let listenerId = readListenerId();
  if (listenerId) {
    writeListenerId(listenerId);
  }

  let poll;
  try {
    poll = await apiJson(
      'GET',
      `/public/watcher/conds/${placementCode}${listenerId ? `?listenerId=${listenerId}` : ''}`,
    );
  } catch (_) {
    poll = await apiJson('GET', `/public/watcher/conds/${placementCode}`);
  }

  if (!poll || !poll.id) {
    return;
  }

  if (!listenerId) {
    listenerId = await apiJson('PUT', '/public/watcher/listener', { placementCode });
    writeListenerId(listenerId);
  }

  // Не открываем popup на F5, даже если reach уже true.
  // Performer — только после trigger === true в этой вкладке (см. fireTrigger).

  bridgeState = {
    pollId: poll.id,
    listenerId,
    placementCode,
    mounts: Array.isArray(poll.mounts) ? poll.mounts : null,
  };
  installNotifyReady();
}

/**
 * Грузим только наш bridge. Их /embed/{code} watcher не вставляем:
 * он дублирует счётчик и сам открывает performer при reach (popup на каждом F5).
 */
export function loadMediastataPollScript(src) {
  if (typeof document === 'undefined' || !src) {
    return () => {};
  }

  ensureProcessPolyfill();
  installNotifyStub();

  const embedSrc = normalizeEmbedSrc(src);
  const placementCode = placementFromEmbedSrc(embedSrc);
  if (!placementCode) {
    return () => {};
  }

  // Маркер в DOM для проверки деплоя (без их watcher-скрипта).
  let marker = document.querySelector('script[data-mediastata-poll="1"]');
  if (!marker) {
    marker = document.createElement('script');
    marker.type = 'application/json';
    marker.dataset.mediastataPoll = '1';
    marker.dataset.mediastataLoader = 'v6';
    marker.text = JSON.stringify({ placementCode, embedSrc });
    document.body.appendChild(marker);
  } else {
    marker.dataset.mediastataLoader = 'v6';
  }

  if (!bridgePromise) {
    bridgePromise = startMediastataBridge(placementCode).catch(() => {
      bridgePromise = null;
    });
  }

  return () => {};
}
